import useTextAreaValidation from '@/components/atoms/TextArea/useTextAreaValidation-hook'
import { TextArea as PepsicoDsTextArea } from 'pepsico-ds'
import { useCallback, useEffect, useState } from 'react'

/**
 * TextArea component renders a text area input field with various properties.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.value - The current value of the text area.
 * @param {Function} props.updateValue - Function to update the value of the text area.
 * @param {string} props.label - The label for the text area.
 * @param {Object} props.uiSchema - UI schema object containing additional configurations.
 * @param {string} [props.uiSchema.helperText=''] - Helper text to display below the text area.
 * @param {string} [props.uiSchema.className=''] - Additional class names for styling.
 * @param {string} [props.uiSchema.placeholder=''] - Placeholder text for the text area.
 * @param {boolean} [props.uiSchema.showToolbar=false] - Whether to show the toolbar.
 * @param {boolean} [props.disabled=false] - Indicates if the text area is disabled.
 * @param {string} [props.id=''] - Optional additional id to apply to the text area.
 * @param {boolean} [props.trim=true] - Whether to automatically trim whitespace from the value.
 * @returns {JSX.Element} The rendered text area component.
 */
export default function TextArea({ value, updateValue = () => { }, label, uiSchema, disabled = false, id, trim = true }) {
  const [errorMessage, setErrorMessage] = useState('')
  const [helperText, setHelperText] = useState(() => uiSchema?.helperText || '')
  const [isFocused, setIsFocused] = useState(false)
  const { onChange } = useTextAreaValidation(label, uiSchema?.validationRules)

  // Clear validation error
  const clearValidationError = useCallback(
    val => {
      setErrorMessage('')
      setHelperText(uiSchema?.helperText || '')
      updateValue(val, false)
    },
    [uiSchema?.helperText, updateValue]
  )

  // Handle validation error
  const handleValidationError = useCallback(
    (error, val) => {
      setErrorMessage(error)
      setHelperText('')
      updateValue(val, true)
    },
    [updateValue]
  )
  const stripHtml = html => {
    const div = document.createElement('div')
    div.innerHTML = html
    return div.textContent || div.innerText || ''
  }
  // Handle onChange
  const handleChange = useCallback(
    e => {
      let newValue = e?.target?.value || e
      // Strip pasted HTML if any
      if (typeof newValue === 'string' && newValue.includes('<')) {
        newValue = stripHtml(newValue)
      }

      // Trim the value if trim is enabled
      newValue = typeof newValue === 'string' && trim ? newValue.trim() : newValue

      const validationError = onChange(newValue)

      if (validationError) {
        handleValidationError(validationError, newValue)
      } else {
        clearValidationError(newValue)
      }
    },
    [onChange, trim, handleValidationError, clearValidationError]
  )

  // Handle onBlur
  const handleBlur = useCallback(() => {
    // Only clear validation if value is empty or undefined
    if (value === undefined || value === null || value === '') {
      clearValidationError('')
      return
    }

    const trimmedValue = trim ? value.trim() : value
    const validationError = onChange(trimmedValue)

    if (validationError) {
      handleValidationError(validationError, trimmedValue)
    } else {
      clearValidationError(trimmedValue)
    }

    !isFocused && setIsFocused(true)
  }, [onChange, trim, handleValidationError, clearValidationError, isFocused, value])

  // Effect to validate initial value
  useEffect(() => {
    if (value) {
      handleChange(value)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  let pepsicoDsId = 'text-area'
  let dataTestId = 'text-area'
  if (id) {
    pepsicoDsId = `text-area_${id}`
    dataTestId = `text-area_${id}`
  }

  return (
    <PepsicoDsTextArea
      id={pepsicoDsId}
      data-testid={dataTestId}
      helperText={helperText}
      feedbackType={errorMessage ? 'error' : 'info'}
      isDark
      label={label}
      className={`${uiSchema?.className || ''} mr-8`}
      value={value || ''}
      disabled={disabled || uiSchema?.readOnly || uiSchema?.disabled}
      placeholder={uiSchema?.placeholder}
      showCharacterCount={false}
      onChange={handleChange}
      onBlur={handleBlur}
      showToolbar={uiSchema?.showToolbar}
      error={errorMessage}
      style={{ width: '100%' }}
    />
  )
}
